import SkybusProviderBase from "../provider/base";
import UnifiedSkybus from "./index";
import OptimisticUnifiedSkybus, {
  OptimisticAnalyticsLogger,
  OptimisticChannelConfig,
} from "./optimisticUnifiedSkybus";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyOptimisticConfig = OptimisticChannelConfig<any, any>;

/**
 * Factory that returns a `UnifiedSkybus` — either the plain class (when no
 * optimistic configs are provided) or the `OptimisticUnifiedSkybus`
 * subclass (when at least one config is provided).
 *
 * Keeping this decision in a single place means:
 *   - Every transport backend (livekit, mock, tests) gets the exact same
 *     selection behavior; we can't forget to wire it up in one place.
 *   - Consumers that don't opt any channel into the optimistic path get a
 *     plain `UnifiedSkybus` with zero behavioral change — useful for
 *     feature-flagging the rollout of optimistic updates.
 *   - It's trivially testable via `instanceof` without any transport
 *     plumbing.
 *
 * `analyticsLogger` is forwarded to the optimistic subclass for structured
 * lifecycle telemetry (request enqueued / published / acked / failed,
 * subscription activation results, etc). It is ignored on the plain
 * `UnifiedSkybus` path.
 */
export function createUnifiedSkybus(
  provider: SkybusProviderBase,
  optimisticConfigs?: ReadonlyArray<AnyOptimisticConfig>,
  analyticsLogger?: OptimisticAnalyticsLogger
): UnifiedSkybus {
  if (optimisticConfigs && optimisticConfigs.length > 0) {
    return new OptimisticUnifiedSkybus(provider, optimisticConfigs, analyticsLogger);
  }
  return new UnifiedSkybus(provider);
}
